import { FileText, PlusCircle, Trash2 } from 'lucide-react'
import { useRef } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { Muted } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type { ChangeEvent } from 'react'

type EnvVar = {
  key: string
  value: string
}

type FormValues = {
  envVars: EnvVar[]
}

type Props = {
  envVars?: EnvVar[]
  onChange?: (envVars: EnvVar[]) => void
  onIsValid?: (isValid: boolean) => void
  isImportButtonDisplayed?: boolean
  isAddButtonDisplayed?: boolean
  isDeleteButtonDisplayed?: boolean
  isPasteEnabled?: boolean
}

export const EnvVariableEditor = ({
  isImportButtonDisplayed = true,
  isAddButtonDisplayed = true,
  isDeleteButtonDisplayed = true,
  isPasteEnabled = true,
}: Props) => {
  const form = useFormContext<FormValues>()
  const { control, getValues } = form

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'envVars',
  })

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handlePaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (!isPasteEnabled) {
      return
    }
    const pastedData = event.clipboardData.getData('text')

    const parsedEnvVars = parseEnvData(pastedData)
    if (parsedEnvVars?.length) {
      event.preventDefault()
      upsertEnvVars(parsedEnvVars, index)
    }
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target?.result as string
      const parsedEnvVars = parseEnvData(content)
      upsertEnvVars(parsedEnvVars)
    }
    reader.readAsText(file)
  }

  const parseEnvData = (data: string): EnvVar[] => {
    return data.split('\n').reduce((acc: EnvVar[], line) => {
      const [key, ...value] = line.split('=')
      if (key?.trim() && value.length && !key?.trim()?.startsWith('#')) {
        acc.push({ key: key.trim(), value: value.join('=').trim() })
      }
      return acc
    }, [])
  }

  const upsertEnvVars = (parsedEnvVars: EnvVar[], startIndex = 0) => {
    if (parsedEnvVars.length > 0) {
      const existingEnvVars = getValues('envVars')

      parsedEnvVars.forEach((parsedVar, parsedIndex) => {
        const existingIndex = existingEnvVars.findIndex(
          (envVar) => envVar.key === parsedVar.key
        )

        if (
          parsedIndex === 0 &&
          startIndex < existingEnvVars.length &&
          !existingEnvVars[startIndex].key
        ) {
          update(startIndex, parsedVar)
        } else if (existingIndex !== -1) {
          update(existingIndex, parsedVar)
        } else {
          append(parsedVar)
        }
      })
    }
  }

  return (
    <div>
      {fields.map((field, index) => (
        <div
          className="flex flex-col gap-2 not-last:mb-4 first:[&>div>button]:mt-5.5"
          key={`env-row-wrapper-${field.id}`}
        >
          <div className="flex items-start gap-2" key={`env-row-${field.id}`}>
            <FormField
              control={control}
              name={`envVars.${index}.key`}
              render={({ field }) => (
                <FormItem className="flex-1">
                  {index === 0 ? <FormLabel>Key</FormLabel> : null}
                  <FormControl>
                    <Input
                      placeholder="e.g. APP_KEY"
                      autoComplete="off"
                      aria-label={`Environment variable key ${index + 1}`}
                      key={`env-key-${index}`}
                      onPaste={(event) => handlePaste(event, index)}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={`envVars.${index}.value`}
              render={({ field }) => (
                <FormItem className="flex-1">
                  {index === 0 ? <FormLabel>Value</FormLabel> : null}
                  <FormControl>
                    <Textarea
                      autoComplete="off"
                      aria-label={`Environment variable value ${index + 1}`}
                      className="min-h-9"
                      key={`env-value-${index}`}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isDeleteButtonDisplayed ? (
              <Button
                key={`env-delete-${index}`}
                aria-label="Delete environment variable"
                type="button"
                variant="ghost"
                disabled={fields.length === 1}
                className="place-self-end self-start hover:text-destructive transition-colors"
                onClick={() => {
                  if (fields.length > 1) {
                    remove(index)
                  }
                }}
              >
                <Trash2
                  key={`env-delete-${index}`}
                  aria-label="Delete environment variable"
                />
              </Button>
            ) : null}
          </div>
        </div>
      ))}
      {isAddButtonDisplayed ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => append({ key: '', value: '' })}
        >
          <PlusCircle aria-hidden="true" /> Add another
        </Button>
      ) : null}
      {isImportButtonDisplayed ? (
        <div className="flex items-center gap-2 mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
          >
            <FileText aria-hidden="true" /> Import .env
          </Button>
          <Muted>or paste the .env contents above</Muted>
          <input
            type="file"
            ref={fileInputRef}
            aria-label="Import .env file"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      ) : null}
    </div>
  )
}
